import {
  motion,
  useScroll,
  useVelocity,
  useTransform,
  useSpring,
  useMotionValue,
  useAnimationFrame,
} from "framer-motion";
import React, { useRef, useState, useEffect } from "react";
import { wrap } from "@motionone/utils";

// --- INTERNAL SUB-COMPONENT: ParallaxText ---
// This is now inside the same file to fix the "Module not found" error.
function ParallaxText({ children, baseVelocity = 100 }) {
  const baseX = useMotionValue(0);
  const { scrollY } = useScroll();
  const scrollVelocity = useVelocity(scrollY);
  const smoothVelocity = useSpring(scrollVelocity, {
    damping: 50,
    stiffness: 400,
  });

  const velocityFactor = useTransform(smoothVelocity, [0, 1000], [0, 5], {
    clamp: false,
  });

  // Infinite wrapping logic
  const x = useTransform(baseX, (v) => `${wrap(-20, -45, v)}%`);

  const directionFactor = useRef(1);
  const [isHovered, setIsHovered] = useState(false);

  useAnimationFrame((t, delta) => {
    if (isHovered) return; // Pauses on hover

    let moveBy = directionFactor.current * baseVelocity * (delta / 1000);

    if (velocityFactor.get() < 0) {
      directionFactor.current = -1;
    } else if (velocityFactor.get() > 0) {
      directionFactor.current = 1;
    }

    moveBy += directionFactor.current * moveBy * velocityFactor.get();
    baseX.set(baseX.get() + moveBy);
  });

  return (
    <div className="flex overflow-hidden m-0 whitespace-nowrap flex-nowrap py-2">
      <motion.div
        className="flex flex-nowrap whitespace-nowrap text-2xl uppercase leading-tight tracking-tighter md:text-5xl"
        style={{ x }} // Skew removed to prevent slanting
        onHoverStart={() => setIsHovered(true)}
        onHoverEnd={() => setIsHovered(false)}
      >
        <span className="block mr-12">{children} </span>
        <span className="block mr-12">{children} </span>
        <span className="block mr-12">{children} </span>
        <span className="block mr-12">{children} </span>
      </motion.div>
    </div>
  );
}

// --- MAIN SECTION COMPONENT ---
export const VelocityText = () => {
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth <= 768);
    checkMobile();
    window.addEventListener("resize", checkMobile);
    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  const baseRows = [
    { v: -0.5, t: "Letter Head. Business Card. Office Envelope. Bill Book / Voucher. A4 Size Envelope." },
    { v: 0.5, t: "Id card lanyard. Bifold / Tri fold Brochure. Poster / Sunpack board. Offset Printing." },
    { v: -0.5, t: "Books Magazines. Rubber Stamp. Paper / Poly Bags. Packaging Design. Award Certificate." },
    { v: 0.5, t: "Corporate Gifting. Flex Printing. Roll Up Standee. Indoor/ Outdoor Signage. Vehicle Signs." },
    { v: -0.5, t: "T-Shirt Printing. Social Media Post design. SEO Services. Social Media Marketing." },
    { v: 0.5, t: "G Suite Services. Digital Business card. Display ads. Commercial ads. Product Photoshoot." },
    { v: -0.6, t: "Video Editing. 360 Video Shoot. Reels & shorts Making. Corporate Profile Video." },
  ];

  const mobileExtraRows = [
    { v: 0.4, t: "Creative Strategy. Brand Identity. Growth Marketing. UI/UX Design." },
    { v: -0.4, t: "Motion Graphics. Logo Animation. Content Writing. Email Campaigns." },
    { v: 0.6, t: "Web Development. App Solutions. E-commerce. Cloud Services." },
    { v: -0.6, t: "Search Engine Optimization. Lead Generation. Data Analytics." },
     { v: -0.5, t: "Letter Head. Business Card. Office Envelope. Bill Book / Voucher. A4 Size Envelope." },
    { v: 0.5, t: "Id card lanyard. Bifold / Tri fold Brochure. Poster / Sunpack board. Offset Printing." },
    { v: 0.5, t: "G Suite Services. Digital Business card. Display ads. Commercial ads. Product Photoshoot." },
    { v: -0.6, t: "Video Editing. 360 Video Shoot. Reels & shorts Making. Corporate Profile Video." },
  ];

  const rows = isMobile ? [...baseRows, ...mobileExtraRows] : baseRows;

  return (
    <section 
      className="bg-white text-black overflow-hidden flex flex-col justify-center min-h-[100dvh] w-full py-4"
      style={{ userSelect: 'none' }}
    >
      <div className="flex flex-col gap-2 md:gap-4">
        {rows.map((row, index) => (
          <ParallaxText key={index} baseVelocity={row.v}>
            {row.t}
          </ParallaxText>
        ))}
      </div>
    </section>
  );
};

export default VelocityText;